import {Select} from "antd";
import React, {memo, useCallback, useMemo} from "react";
import {EnvironmentRoles} from "./UserManager.types";
import {
  getActiveLabel,
  getAdminLabel,
  getInactiveLabel,
  getSuperAdminLabel,
} from "./UserManager.utils";
import {useSelector} from "react-redux";
import {getPermission} from "../../../redux/appEnvironment.selector";
import {PermissionsEnum} from "../../../redux/models/appEnvironment";

interface Props {
  roles: EnvironmentRoles[]
  active: boolean
  isBironUser: boolean
  isCurrentUser: boolean
  disabled: boolean
  onUpdate: (roles: EnvironmentRoles[]) => void
  onUpdateActive: (isActive: boolean) => void
}


export enum EnvironmentRightUpdaterOptions {
  "ENVIRONMENT_ADMIN" = "ENVIRONMENT_ADMIN",
  "ENVIRONMENT_SUPERADMIN" = "ENVIRONMENT_SUPERADMIN",
  "ACTIVE" = "ACTIVE",
  "INACTIVE" = "INACTIVE"
}

const EnvironmentRightUpdater = memo<Props>(({
                                               roles,
                                               active,
                                               disabled,
                                               isBironUser,
                                               isCurrentUser,
                                               onUpdate,
                                               onUpdateActive
                                             }) => {
  const getPermissionSelector = useSelector(getPermission)
  const userCanSetPowerUser = getPermissionSelector?.(PermissionsEnum.userCanManageAdvancedFeature)
  const environmentRightUpdaterOptions = useMemo(() => {
    return [{
      value: EnvironmentRightUpdaterOptions.ACTIVE,
      label: getActiveLabel()
    }, ...(isCurrentUser ? [] : [{
      value: EnvironmentRightUpdaterOptions.INACTIVE,
      label: getInactiveLabel()
    }]), {
      value: EnvironmentRightUpdaterOptions.ENVIRONMENT_ADMIN,
      label: getAdminLabel(),
    }, ...(userCanSetPowerUser && isBironUser ? [{
      value: EnvironmentRightUpdaterOptions.ENVIRONMENT_SUPERADMIN,
      label: getSuperAdminLabel(),
    }] : [])]
  }, [isBironUser, isCurrentUser, userCanSetPowerUser])

  const selectedRoles = useMemo(() => {
    if (roles.includes(EnvironmentRoles.ENVIRONMENT_SUPERADMIN)) {
      return EnvironmentRightUpdaterOptions.ENVIRONMENT_SUPERADMIN
    }

    if (roles.includes(EnvironmentRoles.ENVIRONMENT_ADMIN)) {
      return EnvironmentRightUpdaterOptions.ENVIRONMENT_ADMIN
    }

    if (active) {
      return EnvironmentRightUpdaterOptions.ACTIVE
    }
    return EnvironmentRightUpdaterOptions.INACTIVE
  }, [active, roles])

  const onChange = useCallback((newRoles: EnvironmentRightUpdaterOptions | null) => {
    if (newRoles === EnvironmentRightUpdaterOptions.ENVIRONMENT_SUPERADMIN) {
      onUpdate([EnvironmentRoles.ENVIRONMENT_SUPERADMIN])
      if (!active) {
        onUpdateActive(true)
      }
    } else if (newRoles === EnvironmentRightUpdaterOptions.ENVIRONMENT_ADMIN) {
      onUpdate([EnvironmentRoles.ENVIRONMENT_ADMIN])
      if (!active) {
        onUpdateActive(true)
      }
    } else if (newRoles === EnvironmentRightUpdaterOptions.ACTIVE) {
      onUpdateActive(true)
      onUpdate([])
    } else if (newRoles === EnvironmentRightUpdaterOptions.INACTIVE) {
      onUpdateActive(false)
      onUpdate([])
    }
  }, [active, onUpdate, onUpdateActive])

  return <Select style={{minWidth: 220}} disabled={disabled} popupMatchSelectWidth={true} value={selectedRoles} onChange={onChange}>
    {environmentRightUpdaterOptions.map(element => <Select.Option
      key={element.value} value={element.value}>
      {element.label}
    </Select.Option>)}
  </Select>
})

export default EnvironmentRightUpdater